import { printApi, type RenderKmSession } from '@/lib/api/print'
import {
  beginRenderHistory,
  finishRenderHistory,
  useRenderHistoryStore,
} from '@/stores/renderHistory.store'

const POLL_MS = 1000

type PollWaiter = {
  resolve: (session: RenderKmSession) => void
  reject: (err: Error) => void
}

type PollState = {
  trackId: string
  timer: ReturnType<typeof setInterval> | null
  waiters: PollWaiter[]
  finishing: boolean
}

const polls = new Map<string, PollState>()
const finishedKmTasks = new Set<string>()

function ensureKmRenderTrack(taskUuid: string, startedAt?: string): string {
  const store = useRenderHistoryStore.getState()
  const existingActive = store.active.find(
    (a) => a.source === 'task_km' && a.taskUuid === taskUuid,
  )
  if (existingActive) return existingActive.id

  const pollState = polls.get(taskUuid)
  if (pollState) return pollState.trackId

  return beginRenderHistory('task_km', `Задание ${taskUuid}`, {
    taskUuid,
    startedAt: startedAt ? parseStartedAt(startedAt) : undefined,
  })
}

function parseStartedAt(iso: string): number {
  const t = Date.parse(iso)
  return Number.isFinite(t) ? t : Date.now()
}

function syncActiveStartedAt(taskUuid: string, trackId: string, startedAt: string): void {
  const started = parseStartedAt(startedAt)
  useRenderHistoryStore.setState((state) => ({
    active: state.active.map((row) =>
      row.id === trackId || (row.source === 'task_km' && row.taskUuid === taskUuid)
        ? { ...row, startedAt: started }
        : row,
    ),
  }))
}

function detailFromSession(session: RenderKmSession): string {
  if (session.status === 'completed') {
    return `${session.rendered_count ?? 0} КМ, ${session.boxes_count ?? 0} коробок`
  }
  return session.error?.trim() || 'ошибка рендера'
}

async function pollOnce(taskUuid: string): Promise<void> {
  const state = polls.get(taskUuid)
  if (!state || state.finishing) return

  try {
    const res = await printApi.getRenderKmStatus(taskUuid)
    const session = res.data.item

    if (session.status === 'running') {
      syncActiveStartedAt(taskUuid, state.trackId, session.started_at)
      return
    }

    if (finishedKmTasks.has(taskUuid) || state.finishing) return
    finishedKmTasks.add(taskUuid)
    state.finishing = true
    if (state.timer) {
      clearInterval(state.timer)
      state.timer = null
    }

    if (session.status === 'completed') {
      finishRenderHistory(state.trackId, 'ok', detailFromSession(session), { taskUuid })
      for (const w of state.waiters) w.resolve(session)
    } else {
      const msg = detailFromSession(session)
      finishRenderHistory(state.trackId, 'error', msg, { taskUuid })
      for (const w of state.waiters) w.reject(new Error(msg))
    }
    polls.delete(taskUuid)
  } catch {
    // transient poll errors — retry on next tick
  }
}

function ensurePollLoop(taskUuid: string, trackId: string): void {
  finishedKmTasks.delete(taskUuid)
  let state = polls.get(taskUuid)
  if (!state) {
    state = { trackId, timer: null, waiters: [], finishing: false }
    polls.set(taskUuid, state)
  }
  state.trackId = trackId
  if (state.timer != null) return
  state.timer = setInterval(() => void pollOnce(taskUuid), POLL_MS)
  void pollOnce(taskUuid)
}

export function stopKmRenderPoll(taskUuid: string): void {
  const state = polls.get(taskUuid)
  if (!state) return
  if (state.timer) clearInterval(state.timer)
  polls.delete(taskUuid)
}

export function pollKmRenderUntilDone(taskUuid: string, trackId: string): Promise<RenderKmSession> {
  ensurePollLoop(taskUuid, trackId)
  const state = polls.get(taskUuid)!
  return new Promise((resolve, reject) => {
    state.waiters.push({ resolve, reject })
  })
}

export async function resumeKmRendersFromServer(): Promise<void> {
  try {
    const res = await printApi.listActiveKmRenders()
    const items = res.data
    if (!Array.isArray(items) || items.length === 0) return

    for (const session of items) {
      const taskUuid = session.task_uuid
      const trackId = ensureKmRenderTrack(taskUuid, session.started_at)
      syncActiveStartedAt(taskUuid, trackId, session.started_at)
      void pollKmRenderUntilDone(taskUuid, trackId).catch(() => {})
    }
  } catch {
    // server offline or not admin — ignore
  }
}
